import React, { FC, ReactNode } from 'react';
import Tile from '../../../app/common/components/Tile';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { ImgLogo, mainTileStyles, ServiceLogo, IntroText } from '../sharedStyles';
import { ServiceInfo, ServiceHeader, ServiceStatus } from './ServiceConsentCard.styles';
import ConsentsBlock from './ConsentsBlock';
import { IDSRDeleteResponse } from '../dsr/dsr.interface';
import { DsrDeleteRequestStatus } from '../dsr/DsrDeleteStatus';
import { AppConfig } from '../../../app/appState.interface';
import { DeleteServicesContainer, DeleteServicesLink } from '../dsr/DsrLinkStyles';
import SocialAccountsSection, { SocialProvider } from '../socialAccounts/SocialAccountsSection';

type DsrAppConfig = AppConfig & {
    DSR_ENABLED: boolean;
    SOCIAL_ACCOUNT_MANAGEMENT_ENABLED?: boolean;
};

export type ServiceConsentCardProps = {
    service: {
        name: string;
        logo: string;
        description: string;
        privacySettingsLink: string;
        serviceUuid: string;
        deleteServiceLinkEnabled: boolean;
        social?: {
            google?: SocialProvider;
            facebook?: SocialProvider;
            apple?: SocialProvider;
        } | null;
        unlinkSocialEnabled: boolean;
    };
    appConfig: DsrAppConfig;
    mandatoryConsents: ReactNode[];
    optionalConsents: ReactNode[];
    onExternalLinkClick(): void;
    anyServiceCmpIntegrated: any;
    dsrStatus?: IDSRDeleteResponse;
    openDSRForm(serviceUuid: string): void;
    showDsrDeleteStatusInfo: boolean;
    toggleDsrDeleteStatusInfo(serviceUuid: string, value: boolean): void;
    onUnlinkSocialProvider?: (providerId: string, providerName: string) => void;
};

const ServiceConsentCard: FC<ServiceConsentCardProps> = (props) => {
    const {
        service,
        mandatoryConsents,
        optionalConsents,
        anyServiceCmpIntegrated,
        onExternalLinkClick,
        appConfig,
        dsrStatus,
        openDSRForm,
        showDsrDeleteStatusInfo,
        toggleDsrDeleteStatusInfo,
        onUnlinkSocialProvider,
    } = props;

    // It helps us to enabled/disabled the delete link for the service
    const allowDSRLinks = !dsrStatus || !dsrStatus.success;

    // This flag will be used to disable the optional consents when we raise a service unlink request
    const showOptionalConsent = !dsrStatus || !dsrStatus.success;

    return (
        <Tile localStyles={mainTileStyles} qaId="ucrm-service-consent-card">
            <ServiceInfo>
                <ServiceHeader>
                    <ServiceLogo>
                        <ImgLogo src={service.logo} alt={service.name} data-qa-id={service.name} />
                    </ServiceLogo>
                    <ServiceStatus>
                        {dsrStatus && (
                            <DsrDeleteRequestStatus
                                dsrStatus={dsrStatus}
                                showDsrStatusMessage={showDsrDeleteStatusInfo}
                                toggleDsrStatusMessage={(action) =>
                                    toggleDsrDeleteStatusInfo(service.serviceUuid, action)
                                }
                            />
                        )}
                    </ServiceStatus>
                </ServiceHeader>
                <IntroText className="tst-rsintro">
                    {TranslationHelpers.getFormattedMessage(service.description, {
                        serviceName: service.name,
                    })}
                </IntroText>
                <ConsentsBlock
                    mandatoryConsents={mandatoryConsents}
                    optionalConsents={optionalConsents}
                    showOptionalConsent={showOptionalConsent}
                    privacySettingsLink={service.privacySettingsLink}
                    onExternalTrackingClick={onExternalLinkClick}
                    anyServiceCmpIntegrated={anyServiceCmpIntegrated}
                />
                <SocialAccountsSection
                    socialAccounts={service.social}
                    onUnlinkProvider={onUnlinkSocialProvider || (() => {})}
                    appConfig={appConfig}
                    disabled={!showOptionalConsent || !service.unlinkSocialEnabled}
                />
                {appConfig.DSR_ENABLED && allowDSRLinks && (
                    <React.Fragment>
                        {service.deleteServiceLinkEnabled && (
                            <div data-qa-id="dsr-link">
                                <DeleteServicesContainer>
                                    <DeleteServicesLink
                                        onClick={() => openDSRForm(service.serviceUuid)}
                                        data-qa-id="dsr-delete-service-link-message"
                                    >
                                        {TranslationHelpers.getFormattedMessage(
                                            'dsr.delete_link_message',
                                        )}
                                    </DeleteServicesLink>
                                </DeleteServicesContainer>
                            </div>
                        )}
                    </React.Fragment>
                )}
            </ServiceInfo>
        </Tile>
    );
};

export default ServiceConsentCard;
